import React from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { trim } from 'ramda'

import { getHotspotCTAUrl, getTwoGenres } from '../../../lib/utils'
import { COMPONENT_NAME_HOTSPOT } from '../../../lib/analytics/custom-dimensions'
import HOTSPOT_SLIDE_FRAGMENT from '../../../../graphql/fragments/hotspot-slide.gql'
import styles from './user-content-cta-box.css'
import CTABoxHeaderImage from '../hotspot-header-image/hotspot-header-image'
import Button from '../../button'
import MyListButton from '../../shared/my-list/my-list-button'
import { Rating } from '../../rating'
import TitleTreatmentImage from '../../title-treatment-image'

const UserContentCTABox = ({
  slide,
  title,
  onCtaClick,
  usableOnClick,
  LinkNode,
  isRental,
  isAuthenticated,
  isOnMyList,
  addToMyList
}) => {
  const {
    badge,
    subHeader,
    contentItem,
    primaryCta,
    headerImageUri,
    disableGradient
  } = slide
  const rating = contentItem.rating
  const url = getHotspotCTAUrl(contentItem)
  // Only Title or Series can be added to my list
  const showMyListButton = ['Series', 'Title'].includes(contentItem.__typename)
  const normalizedBadge = typeof badge === 'string' ? trim(badge) : ''
  const genres = getTwoGenres(contentItem)
    .filter((item) => typeof item === 'string' && trim(item))
    .map((item) => (
      <span
        key={item}
        className={styles.tag}
      >
        {item}
      </span>
    ))

  return (
    <div className={styles.wrapper}>
      <div>
        <div className={classNames({ [styles.sideGradient]: !disableGradient })} />
        <div className={classNames({ [styles.bottomGradient]: !disableGradient })} />

        <div className={styles.ctaBox}>
          <CTABoxHeaderImage headerImageUri={headerImageUri} />
          <div className={styles.headers}>
            { contentItem.titleTreatment ? (
              <div className={styles.titleTreatmentFrame}>
                <TitleTreatmentImage src={contentItem.titleTreatment} alt="Title Treatment" />
              </div>
            ) : (
              <div className={styles.title}>{title}</div>
            )}
          </div>
          <div className={styles.subHeader}>{subHeader}</div>
          <div className={styles.box}>
            {rating && (
              <Rating rating={rating} className={styles.ratingLabel} />
            )}
            {
              normalizedBadge && (
                <span className={classNames(styles.tag, styles.firstTag)}>{normalizedBadge}</span>
              )
            }
            {genres}
          </div>
          <div className={styles.buttonsWrapper}>
            {
              primaryCta && (
                <LinkNode
                  onClick={usableOnClick(() => onCtaClick(url, contentItem))}
                  to={url}
                >
                  <Button
                    text={primaryCta}
                    e2eIdentifier="user-hotspot-cta-button"
                    isRental={isRental}
                    darkBGSecondary
                  />
                </LinkNode>
              )
            }
            {
              isAuthenticated && showMyListButton && (
                <MyListButton
                  contentItem={contentItem}
                  isOnMyList={isOnMyList}
                  addAction={addToMyList(COMPONENT_NAME_HOTSPOT)}
                  isRental={isRental}
                  isBigIcon
                />
              )
            }
          </div>
        </div>
      </div>
    </div>
  )
}

UserContentCTABox.propTypes = {
  slide: propType(HOTSPOT_SLIDE_FRAGMENT).isRequired,
  onCtaClick: PropTypes.func.isRequired,
  usableOnClick: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isRental: PropTypes.bool.isRequired,
  isOnMyList: PropTypes.bool.isRequired,
  addToMyList: PropTypes.func.isRequired,
  title: PropTypes.string,
  LinkNode: PropTypes.oneOfType([PropTypes.object])
}

UserContentCTABox.defaultProps = {
  title: '',
  LinkNode: Link
}

export default UserContentCTABox
